import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PrimaryNavRail } from '../../components/map/PrimaryNavRail';
import { Logo } from '../../components/brand/Logo';
import { useAdminData } from '../../admin/AdminDataContext';
import type { AdminLayer, LayerStatus } from '../../admin/types';
import { api, ApiError } from '../../lib/api';
import { LayerAccessModal } from '../../components/admin/LayerAccessModal';
import { LayerHistoryModal } from '../../components/admin/LayerHistoryModal';
import { dictionariesApi } from '../../lib/dictionariesApi';
import { layersApi } from '../../lib/layersApi';
import { canManageLayers } from '../../lib/tokens';
import type { Me } from '../../lib/types';

/** Maps the backend's machine-readable source_type (gis.layers.source_type:
 * 'internal' | 'wms' | 'wfs' | 'wmts' | 'ogc_api' | 'file' | 'database' | 'api' | 'raster')
 * to a display label. This used to just uppercase the raw value and check it against
 * a set of already-uppercase labels, which only ever matched wms/wfs/wmts by
 * coincidence — a PostGIS-imported layer (source_type='database') fell through to
 * the generic 'File' fallback along with every other non-OGC source. */
const sourceTypeLabels: Record<string, AdminLayer['source']> = {
  internal: 'File',
  file: 'File',
  wms: 'WMS',
  wfs: 'WFS',
  wmts: 'WMTS',
  ogc_api: 'GeoJSON',
  database: 'PostGIS',
  api: 'API',
  raster: 'Raster',
};

function sourceLabel(sourceType: string): AdminLayer['source'] {
  return sourceTypeLabels[sourceType] ?? 'File';
}

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

const statusLabel: Record<LayerStatus, string> = {
  published: 'Опубликован',
  review: 'Неопубликован',
  draft: 'Черновик',
};

const statusClass: Record<LayerStatus, string> = {
  published: 'bg-status-success text-text-inverse',
  review: 'bg-status-warning text-text-inverse',
  draft: 'bg-ai-accent text-text-inverse',
};

/** "Опубликован"/"Неопубликован" isn't a separate backend field — it's just
 * whatever "Показывать на карте" (is_visible) currently is. 'draft' is the one
 * real distinct backend status (the "empty layer" wizard's save-and-finish-later),
 * everything else collapses to published/review by visibility. */
function computeLayerStatus(status: string, isVisible: boolean): LayerStatus {
  if (status === 'draft') return 'draft';
  return isVisible ? 'published' : 'review';
}

const quickFilters: { key: LayerStatus; label: string }[] = [
  { key: 'published', label: 'Опубликованные' },
  { key: 'review', label: 'Неопубликованные' },
  { key: 'draft', label: 'Черновики' },
];

const geometryTypeLabels: Record<string, string> = {
  point: 'Точки',
  multipoint: 'Мультиточки',
  line: 'Линии',
  multiline: 'Мультилинии',
  polygon: 'Полигоны',
  multipolygon: 'Мультиполигоны',
  mixed: 'Смешанная геометрия',
  raster: 'Растр',
  none: 'Без геометрии',
};

type SortKey = 'updated' | 'objectCount' | 'name';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: 'По дате обновления' },
  { key: 'objectCount', label: 'По количеству объектов' },
  { key: 'name', label: 'По алфавиту' },
];

/** <details> instead of React state on purpose — the open/closed flag only
 * needs to be an initial value (collapsed by default), and native disclosure
 * toggling doesn't need re-renders to work. `open` here is a constant per
 * section, so React never re-applies it and never fights a manual toggle. */
function FilterSection({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: ReactNode }) {
  return (
    <details className="group" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between text-[13px] font-semibold text-text-primary marker:content-none [&::-webkit-details-marker]:hidden">
        {title}
        <span className="text-[10px] text-text-secondary transition-transform group-open:rotate-180">▾</span>
      </summary>
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </details>
  );
}

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`flex h-[26px] w-[52px] shrink-0 items-center rounded-full px-1 transition-colors disabled:opacity-50 ${
        checked ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'
      }`}
    >
      <span className="size-[18px] rounded-full bg-bg-surface shadow" />
    </button>
  );
}

export function DataLayersPage() {
  const [me, setMe] = useState<Me | null>(null);
  const { mapSettings, basemaps } = useAdminData();
  const [layers, setLayers] = useState<AdminLayer[]>([]);

  const [selectedGroup, setSelectedGroup] = useState<string>('Все слои');
  const [activeStatusFilters, setActiveStatusFilters] = useState<Set<LayerStatus>>(new Set());
  const [activeGeometryFilters, setActiveGeometryFilters] = useState<Set<string>>(new Set());
  const [activeSourceFilters, setActiveSourceFilters] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortKey>('updated');
  const [search, setSearch] = useState('');
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [dictionaryCount, setDictionaryCount] = useState(0);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    dictionariesApi
      .listDictionaries()
      .then((rows) => setDictionaryCount(rows.length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!mapSettings?.id) return;
    layersApi
      .listLayers(mapSettings.id)
      .then((dtos) =>
        setLayers(
          dtos.map((dto) => ({
            id: dto.id,
            name: dto.name,
            code: dto.code,
            group: dto.group ?? 'Без группы',
            geometryType: dto.geometry_type,
            source: sourceLabel(dto.source),
            status: computeLayerStatus(dto.status, dto.is_visible),
            objectCount: dto.object_count,
            version: dto.version,
            updatedAt: new Date(dto.updated_at).toLocaleString('ru-RU'),
            updatedAtRaw: dto.updated_at,
            crs: dto.crs,
            isVisible: dto.is_visible,
          })),
        ),
      )
      .catch(() => setLayers([]));
  }, [mapSettings?.id]);

  const groups = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of layers) counts.set(l.group, (counts.get(l.group) ?? 0) + 1);
    return [{ name: 'Все слои', count: layers.length }, ...Array.from(counts.entries()).map(([name, count]) => ({ name, count }))];
  }, [layers]);

  const geometryOptions = useMemo(() => {
    const set = new Set(layers.map((l) => l.geometryType));
    return Array.from(set)
      .sort()
      .map((key) => ({ key, label: geometryTypeLabels[key] ?? key }));
  }, [layers]);

  const sourceOptions = useMemo(() => {
    const set = new Set(layers.map((l) => l.source));
    return Array.from(set).sort();
  }, [layers]);

  const toggleGeometryFilter = (key: string) => {
    setActiveGeometryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSourceFilter = (key: string) => {
    setActiveSourceFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredLayers = useMemo(() => {
    const filtered = layers.filter((l) => {
      if (selectedGroup !== 'Все слои' && l.group !== selectedGroup) return false;
      if (activeStatusFilters.size > 0 && !activeStatusFilters.has(l.status)) return false;
      if (activeGeometryFilters.size > 0 && !activeGeometryFilters.has(l.geometryType)) return false;
      if (activeSourceFilters.size > 0 && !activeSourceFilters.has(l.source)) return false;
      if (search.trim() && !l.name.toLowerCase().includes(search.trim().toLowerCase()) && !l.code.includes(search.trim())) {
        return false;
      }
      return true;
    });
    const sorted = [...filtered];
    if (sortBy === 'updated') sorted.sort((a, b) => b.updatedAtRaw.localeCompare(a.updatedAtRaw));
    else if (sortBy === 'objectCount') sorted.sort((a, b) => (b.objectCount ?? -1) - (a.objectCount ?? -1));
    else sorted.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    return sorted;
  }, [layers, selectedGroup, activeStatusFilters, activeGeometryFilters, activeSourceFilters, sortBy, search]);

  const selectedLayer: AdminLayer | undefined =
    layers.find((l) => l.id === selectedLayerId) ?? filteredLayers[0];

  const toggleStatusFilter = (status: LayerStatus) => {
    setActiveStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handleToggleVisible = async () => {
    if (!selectedLayer) return;
    const nextVisible = !selectedLayer.isVisible;
    setVisibilityError(null);
    setTogglingVisibility(true);
    try {
      await layersApi.setLayerVisible(selectedLayer.id, nextVisible);
      setLayers((prev) =>
        prev.map((l) =>
          l.id === selectedLayer.id
            ? { ...l, isVisible: nextVisible, status: nextVisible ? 'published' : 'review' }
            : l,
        ),
      );
    } catch (err) {
      setVisibilityError(err instanceof ApiError ? err.message : 'Не удалось изменить видимость слоя.');
    } finally {
      setTogglingVisibility(false);
    }
  };

  const isVectorLayer = (layer: AdminLayer) => layer.geometryType !== 'raster' && layer.geometryType !== 'none';

  const handleDeleteLayer = async () => {
    if (!selectedLayer) return;
    if (!window.confirm(`Удалить слой «${selectedLayer.name}»? Это действие нельзя отменить.`)) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await layersApi.deleteLayer(selectedLayer.id);
      setLayers((prev) => prev.filter((l) => l.id !== selectedLayer.id));
      setSelectedLayerId(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Не удалось удалить слой.');
    } finally {
      setDeleting(false);
    }
  };

  const handlePublishLayer = async () => {
    if (!selectedLayer) return;
    setPublishError(null);
    setPublishing(true);
    try {
      const dto = await layersApi.publishLayer(selectedLayer.id);
      setLayers((prev) =>
        prev.map((l) =>
          l.id === selectedLayer.id
            ? { ...l, status: computeLayerStatus(dto.status, dto.is_visible), isVisible: dto.is_visible }
            : l,
        ),
      );
    } catch (err) {
      setPublishError(err instanceof ApiError ? err.message : 'Не удалось опубликовать слой.');
    } finally {
      setPublishing(false);
    }
  };

  const handleExportLayer = async () => {
    if (!selectedLayer) return;
    setExportError(null);
    setExporting(true);
    try {
      const fc = await layersApi.getFeatures(selectedLayer.id);
      const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/geo+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedLayer.code}.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Не удалось экспортировать слой.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg-canvas">
      <PrimaryNavRail userInitials={initialsOf(me)} userAvatarUrl={me?.avatar_url} />

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="sticky top-0 z-20 flex h-[72px] shrink-0 items-center gap-3 border-b border-border-default bg-bg-surface px-8">
          <Logo variant="light" size={18} />
          <span className="text-text-secondary">/</span>
          <p className="text-sm text-text-secondary">Управление данными</p>
          {canManageLayers() && (
            <div className="ml-auto flex items-center gap-3">
              <Link
                to="/app/data/add"
                className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                + Добавить данные
              </Link>
            </div>
          )}
        </header>

        <div className="px-8 pt-6">
          <h1 className="text-[28px] font-semibold text-text-primary">Данные и слои</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Управление слоями, подключениями, справочниками и публикацией данных
          </p>

          <div className="mt-5 flex items-center gap-2">
            <span className="flex h-[38px] items-center rounded-[10px] bg-action-primary px-4 text-xs font-medium text-text-inverse">
              Слои {layers.length}
            </span>
            <Link
              to="/app/dictionaries"
              className="flex h-[38px] items-center px-4 text-xs font-medium text-text-secondary hover:text-text-primary"
            >
              Справочники {dictionaryCount}
            </Link>
            <Link
              to="/app/map-settings"
              className="flex h-[38px] items-center px-4 text-xs font-medium text-text-secondary hover:text-text-primary"
            >
              Подложки {basemaps.length}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-[318px_1fr_320px] gap-6 px-8 py-6">
          <aside className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <FilterSection title="Группы слоёв">
              <div className="flex flex-col gap-1">
                {groups.map((g) => (
                  <button
                    key={g.name}
                    type="button"
                    onClick={() => setSelectedGroup(g.name)}
                    className={`flex items-center justify-between rounded-[10px] px-3 py-2.5 text-left text-[13px] transition-colors ${
                      selectedGroup === g.name
                        ? 'bg-bg-surface-secondary font-semibold text-text-primary'
                        : 'font-medium text-text-secondary hover:bg-bg-surface-secondary'
                    }`}
                  >
                    <span>{g.name}</span>
                    <span
                      className={`flex h-[26px] min-w-[32px] items-center justify-center rounded-lg px-2 text-[11px] font-medium ${
                        selectedGroup === g.name
                          ? 'bg-action-primary text-text-inverse'
                          : 'bg-bg-surface-secondary text-text-primary'
                      }`}
                    >
                      {g.count}
                    </span>
                  </button>
                ))}
              </div>
            </FilterSection>

            <div className="my-5 h-px bg-border-default" />

            <p className="text-[13px] font-semibold text-text-primary">Быстрые фильтры</p>
            <div className="mt-3 flex flex-col gap-3">
              {quickFilters.map((f) => (
                <label key={f.key} className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={activeStatusFilters.has(f.key)}
                    onChange={() => toggleStatusFilter(f.key)}
                    className="size-[18px] rounded-[5px] accent-action-primary"
                  />
                  <span className="text-xs font-medium text-text-secondary">{f.label}</span>
                </label>
              ))}
            </div>

            {geometryOptions.length > 0 && (
              <>
                <div className="my-5 h-px bg-border-default" />
                <FilterSection title="Геометрия">
                  {geometryOptions.map((g) => (
                    <label key={g.key} className="flex cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={activeGeometryFilters.has(g.key)}
                        onChange={() => toggleGeometryFilter(g.key)}
                        className="size-[18px] rounded-[5px] accent-action-primary"
                      />
                      <span className="text-xs font-medium text-text-secondary">{g.label}</span>
                    </label>
                  ))}
                </FilterSection>
              </>
            )}

            {sourceOptions.length > 0 && (
              <>
                <div className="my-5 h-px bg-border-default" />
                <FilterSection title="Источник">
                  {sourceOptions.map((s) => (
                    <label key={s} className="flex cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={activeSourceFilters.has(s)}
                        onChange={() => toggleSourceFilter(s)}
                        className="size-[18px] rounded-[5px] accent-action-primary"
                      />
                      <span className="text-xs font-medium text-text-secondary">{s}</span>
                    </label>
                  ))}
                </FilterSection>
              </>
            )}
          </aside>

          <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-[17px] font-semibold text-text-primary">Реестр слоёв</h2>
                <p className="text-xs text-text-secondary">{filteredLayers.length} слоя</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortKey)}
                  className="h-10 rounded-[10px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none"
                >
                  {sortOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Поиск по названию, коду или источнику"
                  className="h-10 w-[300px] rounded-[10px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-[2fr_1.4fr_1fr_0.8fr_0.6fr_1fr] gap-2 rounded-lg bg-bg-surface-secondary px-4 py-2.5">
              <span className="text-[11px] font-semibold text-text-secondary">Слой</span>
              <span className="text-[11px] font-semibold text-text-secondary">Тип / источник</span>
              <span className="text-[11px] font-semibold text-text-secondary">Статус</span>
              <span className="text-[11px] font-semibold text-text-secondary">Объектов</span>
              <span className="text-[11px] font-semibold text-text-secondary">Версия</span>
              <span className="text-[11px] font-semibold text-text-secondary">Обновлён</span>
            </div>

            <div className="flex flex-col">
              {filteredLayers.map((layer) => (
                <button
                  key={layer.id}
                  type="button"
                  onClick={() => setSelectedLayerId(layer.id)}
                  className={`grid grid-cols-[2fr_1.4fr_1fr_0.8fr_0.6fr_1fr] items-center gap-2 rounded-lg border-b border-border-default px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-bg-surface-secondary ${
                    selectedLayer?.id === layer.id ? 'bg-bg-surface-secondary' : ''
                  }`}
                >
                  <span>
                    <span className="block text-[13px] font-semibold text-text-primary">{layer.name}</span>
                    <span className="block text-[10px] text-text-secondary">{layer.code}</span>
                  </span>
                  <span className="text-xs font-medium text-text-secondary">
                    {layer.geometryType} · {layer.source}
                  </span>
                  <span
                    className={`flex h-[26px] w-fit items-center rounded-lg px-2.5 text-[11px] font-medium ${statusClass[layer.status]}`}
                  >
                    {statusLabel[layer.status]}
                  </span>
                  <span className="text-xs font-medium text-text-primary">
                    {layer.objectCount != null ? layer.objectCount.toLocaleString('ru-RU') : '—'}
                  </span>
                  <span className="text-xs font-medium text-text-primary">v{layer.version}</span>
                  <span className="text-[11px] text-text-secondary">{layer.updatedAt}</span>
                </button>
              ))}
              {filteredLayers.length === 0 && (
                <p className="py-8 text-center text-xs text-text-secondary">Ничего не найдено</p>
              )}
            </div>
          </section>

          <aside className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            {selectedLayer ? (
              <>
                <p className="text-[13px] font-medium text-text-secondary">Выбранный слой</p>
                <h3 className="mt-2 text-xl font-semibold text-text-primary">{selectedLayer.name}</h3>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`flex h-[26px] items-center rounded-lg px-2.5 text-[11px] font-medium ${statusClass[selectedLayer.status]}`}>
                    {statusLabel[selectedLayer.status]}
                  </span>
                  <span className="flex h-[26px] items-center rounded-lg bg-bg-surface-secondary px-2.5 text-[11px] font-medium text-text-primary">
                    v{selectedLayer.version}
                  </span>
                </div>

                <p className="mt-5 text-[11px] font-medium text-text-secondary">Источник</p>
                <p className="mt-1 text-[13px] font-semibold text-text-primary">{selectedLayer.source}</p>

                <p className="mt-4 text-[11px] font-medium text-text-secondary">Геометрия</p>
                <p className="mt-1 text-[13px] font-medium text-text-primary">
                  {selectedLayer.geometryType} · {selectedLayer.crs ?? '—'}
                </p>

                <p className="mt-4 text-[11px] font-medium text-text-secondary">Объектов</p>
                <p className="mt-1 text-[22px] font-semibold text-text-primary">
                  {selectedLayer.objectCount != null ? selectedLayer.objectCount.toLocaleString('ru-RU') : '—'}
                </p>

                <div className="my-5 h-px bg-border-default" />
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[13px] font-medium text-text-primary">Показывать на карте</span>
                  <ToggleSwitch
                    checked={selectedLayer.isVisible ?? true}
                    onChange={handleToggleVisible}
                    disabled={togglingVisibility || !canManageLayers() || selectedLayer.status === 'draft'}
                  />
                </div>
                {selectedLayer.status === 'draft' && (
                  <p className="mt-2 text-[11px] text-text-secondary">Черновик не показывается на карте — опубликуйте слой</p>
                )}
                {visibilityError && <p className="mt-2 text-[11px] font-medium text-status-danger">{visibilityError}</p>}

                <div className="my-5 h-px bg-border-default" />

                <p className="text-[13px] font-semibold text-text-primary">Управление</p>
                <div className="mt-3 flex flex-col gap-2">
                  <Link
                    to={`/app/data/layers/${selectedLayer.id}/table`}
                    className="flex h-10 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
                  >
                    Открыть данные
                  </Link>
                  {canManageLayers() && (
                    <Link
                      to={`/app/data/edit/${selectedLayer.id}`}
                      className="flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                    >
                      Редактировать слой
                    </Link>
                  )}
                  {selectedLayer.status === 'draft' && canManageLayers() && (
                    <button
                      type="button"
                      onClick={handlePublishLayer}
                      disabled={publishing}
                      title="Опубликовать — слой появится на карте"
                      className="flex h-10 items-center justify-center rounded-[10px] bg-status-success text-[13px] font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {publishing ? 'Публикуем…' : 'Опубликовать слой'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleExportLayer}
                    disabled={exporting || !isVectorLayer(selectedLayer)}
                    title={isVectorLayer(selectedLayer) ? 'Скачать объекты слоя в формате GeoJSON' : 'Экспорт доступен только для векторных слоёв'}
                    className="flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {exporting ? 'Экспортируем…' : 'Экспортировать слой'}
                  </button>
                  {canManageLayers() && (
                    <button
                      type="button"
                      onClick={() => setHistoryModalOpen(true)}
                      className="flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                    >
                      История изменений
                    </button>
                  )}
                  {canManageLayers() && (
                    <button
                      type="button"
                      onClick={() => setAccessModalOpen(true)}
                      className="flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                    >
                      Права доступа
                    </button>
                  )}
                  {canManageLayers() && (
                    <button
                      type="button"
                      onClick={handleDeleteLayer}
                      disabled={deleting}
                      className="flex h-10 items-center justify-center rounded-[10px] border border-status-danger text-[13px] font-medium text-status-danger hover:bg-status-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deleting ? 'Удаляем…' : 'Удалить слой'}
                    </button>
                  )}
                </div>
                {publishError && <p className="mt-2 text-[11px] font-medium text-status-danger">{publishError}</p>}
                {exportError && <p className="mt-2 text-[11px] font-medium text-status-danger">{exportError}</p>}
                {deleteError && <p className="mt-2 text-[11px] font-medium text-status-danger">{deleteError}</p>}
              </>
            ) : (
              <p className="text-xs text-text-secondary">Выберите слой в реестре</p>
            )}
          </aside>
        </div>
      </div>

      {accessModalOpen && selectedLayer && (
        <LayerAccessModal
          layerId={selectedLayer.id}
          layerName={selectedLayer.name}
          onClose={() => setAccessModalOpen(false)}
        />
      )}

      {historyModalOpen && selectedLayer && (
        <LayerHistoryModal
          layerId={selectedLayer.id}
          layerName={selectedLayer.name}
          onClose={() => setHistoryModalOpen(false)}
        />
      )}
    </div>
  );
}
